import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { zipLocation } from '../../util/api';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { boundsAround, BUY_IT_NOW_LISTING_TYPES } from './nearby';

const { LatLng, LatLngBounds } = sdkTypes;

const PER_PAGE = 100;

const fetchNearbyPayloadCreator = async (arg, thunkAPI) => {
  const { extra: sdk, dispatch, rejectWithValue } = thunkAPI;
  const { zip, radius, type, config } = arg;
  const key = `${zip}|${radius}|${type}`;

  let origin;
  try {
    const res = await zipLocation(zip);
    origin = { lat: res.lat, lng: res.lng };
  } catch (e) {
    return rejectWithValue({
      key,
      message:
        e?.code === 'zip-not-found'
          ? "We couldn't find that ZIP code. Check it and try again."
          : 'Something went wrong looking up that ZIP code. Please try again.',
    });
  }

  const { ne, sw } = boundsAround(origin, radius);
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    config?.layout?.listingImage || {};
  const aspectRatio = aspectHeight / aspectWidth;

  try {
    const response = await sdk.listings.query({
      origin: new LatLng(origin.lat, origin.lng),
      bounds: new LatLngBounds(new LatLng(ne.lat, ne.lng), new LatLng(sw.lat, sw.lng)),
      ...(type === 'buy-it-now' ? { pub_listingType: BUY_IT_NOW_LISTING_TYPES.join(',') } : {}),
      perPage: PER_PAGE,
      page: 1,
      minStock: 1,
      stockMode: 'match-undefined',
      include: ['images', 'author'],
      'fields.listing': [
        'title',
        'geolocation',
        'price',
        'deleted',
        'state',
        'createdAt',
        'publicData.listingType',
        'publicData.categoryLevel1',
        'publicData.location',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        'publicData.cardStyle',
        'publicData.pickupEnabled',
        'publicData.shippingEnabled',
        'publicData.priceVariationsEnabled',
        'publicData.priceVariants',
        'publicData.verifieddealer',
      ],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.metadata'],
      'fields.image': [
        'variants.listing-card',
        'variants.listing-card-2x',
        'variants.scaled-small',
        'variants.scaled-medium',
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    });
    dispatch(addMarketplaceEntities(response));
    return { key, origin, listingIds: response.data.data.map(l => l.id) };
  } catch (e) {
    log.error(e, 'nearby-listings-failed', { zip, radius, type });
    return rejectWithValue({ key, ...storableError(e), message: 'Could not load listings.' });
  }
};

export const fetchNearbyListings = createAsyncThunk(
  'NearbyPage/fetchNearbyListings',
  fetchNearbyPayloadCreator
);

const initialState = { key: null, origin: null, listingIds: [], inProgress: false, error: null };

const nearbyPageSlice = createSlice({
  name: 'NearbyPage',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchNearbyListings.pending, (state, action) => {
        const { zip, radius, type } = action.meta.arg;
        state.inProgress = true;
        state.error = null;
        state.key = `${zip}|${radius}|${type}`;
        state.listingIds = [];
        state.origin = null;
      })
      .addCase(fetchNearbyListings.fulfilled, (state, action) => {
        if (action.payload.key !== state.key) return;
        state.inProgress = false;
        state.origin = action.payload.origin;
        state.listingIds = action.payload.listingIds;
      })
      .addCase(fetchNearbyListings.rejected, (state, action) => {
        if (action.payload?.key && action.payload.key !== state.key) return;
        state.inProgress = false;
        state.error = action.payload || { message: 'Could not load listings.' };
      });
  },
});

export default nearbyPageSlice.reducer;
